/**
 * CalendarContextMenu — the GENERIC renderer over the action registry.
 *
 * It knows nothing about appointments or breaks: it receives a built
 * ActionContext, filters the registry, renders groups with separators, and
 * runs whatever the user picks. One keydown handler serves every menu-local
 * hotkey (the legacy menu hand-wrote a switch per event type); ArrowUp/Down +
 * Enter give roving focus; Escape and click-away close.
 *
 * Deliberately NOT MUI (house direction) and not radix DropdownMenu (its
 * trigger-anchored positioning fights coordinate menus): a fixed-position
 * card, styled to match the shadcn popover look, clamped to the viewport.
 */
import React, { useEffect, useLayoutEffect, useRef } from "react";
import {
  ActionContext,
  ActionServices,
  CalendarAction,
  actionLabel,
} from "../actions";
import { actionsForContext } from "../actions";

export interface CalendarContextMenuState {
  ctx: ActionContext;
  x: number;
  y: number;
}

interface CalendarContextMenuProps {
  state: CalendarContextMenuState;
  services: ActionServices;
  onClose: () => void;
}

const GROUP_ORDER: ReadonlyArray<CalendarAction["group"]> = ["navigate", "modify", "danger"];

const CalendarContextMenu: React.FC<CalendarContextMenuProps> = ({
  state,
  services,
  onClose,
}) => {
  const { ctx, x, y } = state;
  const rootRef = useRef<HTMLDivElement>(null);
  const actions = actionsForContext(ctx);

  const runAction = (action: CalendarAction) => {
    onClose();
    void action.run(ctx as never, services);
  };

  // Clamp into the viewport once rendered (menus near the bottom/right edge).
  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const nx = Math.min(x, window.innerWidth - rect.width - 8);
    const ny = Math.min(y, window.innerHeight - rect.height - 8);
    el.style.left = `${Math.max(8, nx)}px`;
    el.style.top = `${Math.max(8, ny)}px`;
    // Focus the first item so ArrowDown/Enter work immediately.
    el.querySelector<HTMLButtonElement>("button[data-menu-item]")?.focus();
  }, [x, y]);

  // Close on outside pointerdown / Escape; menu-local hotkeys run actions.
  useEffect(() => {
    const onPointerDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) onClose();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation(); // must not fall through to grid staged-clear
        onClose();
        return;
      }
      const items = Array.from(
        rootRef.current?.querySelectorAll<HTMLButtonElement>("button[data-menu-item]") ?? [],
      );
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        const idx = items.indexOf(document.activeElement as HTMLButtonElement);
        const next =
          e.key === "ArrowDown"
            ? items[(idx + 1) % items.length]
            : items[(idx - 1 + items.length) % items.length];
        next?.focus();
        return;
      }
      if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
        const hit = actions.find((a) => a.hotkey?.toLowerCase() === e.key.toLowerCase());
        if (hit) {
          e.preventDefault();
          e.stopPropagation(); // don't leak into the grid's global hotkeys
          runAction(hit);
        }
      }
    };
    document.addEventListener("pointerdown", onPointerDown, { capture: true });
    document.addEventListener("keydown", onKeyDown, { capture: true });
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, { capture: true });
      document.removeEventListener("keydown", onKeyDown, { capture: true });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actions, onClose, ctx]);

  if (actions.length === 0) return null;

  return (
    <div
      ref={rootRef}
      role="menu"
      aria-label="Termin-Aktionen"
      className="fixed z-[1400] min-w-[230px] rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-md"
      style={{ left: x, top: y }}
      onContextMenu={(e) => e.preventDefault()}
    >
      {GROUP_ORDER.map((group) => {
        const groupActions = actions.filter((a) => a.group === group);
        if (groupActions.length === 0) return null;
        return (
          <div key={group} className="border-b border-border py-1 last:border-b-0 last:pb-0 first:pt-0">
            {groupActions.map((action) => {
              const Icon = action.icon;
              return (
                <button
                  key={action.id}
                  type="button"
                  data-menu-item
                  role="menuitem"
                  onClick={() => runAction(action)}
                  className={[
                    "flex w-full cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none",
                    "hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
                    group === "danger" ? "text-destructive hover:text-destructive focus:text-destructive" : "",
                  ].join(" ")}
                >
                  <Icon className="h-4 w-4 shrink-0 opacity-70" />
                  <span className="flex-1 truncate">{actionLabel(action, ctx)}</span>
                  {action.hotkey && (
                    <span className="ml-4 text-xs uppercase tracking-wide text-muted-foreground">
                      {action.hotkey}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

export default CalendarContextMenu;
